/**
 * Página de gestión de Cuentas del Backoffice Operativo.
 * Listado con filtros, historial de transacciones y edición de estado.
 *
 * @taskId T473, T464
 * @see functional/Specs/specs_cycle_4.md HU-27-FE, HU-15-FE
 */

import { useCallback, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import { Button, Eyebrow } from '@flexibilitypay/ui';

import {
  AccountFilters,
  AccountListTable,
  AccountHistoryModal,
  AccountEditStatusModal,
  TransactionDetailModal,
  useAccounts,
  useExportAccountsCsv,
} from '../features/accounts';
import type { AccountStatus, AccountSummary, TransactionSummary } from '../features/accounts';
import { CloseAccountModal } from '../features/closure';
import { useAuth } from '../hooks/use-auth';
import { ROUTES } from '../routes/routes-config';

const PAGE_SIZE = 10;

export function AccountsBoPage() {
  const { t } = useTranslation(['accounts', 'common']);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Estado de UI
  const [selectedAccountForHistory, setSelectedAccountForHistory] = useState<AccountSummary | null>(null);
  const [selectedAccountForEdit, setSelectedAccountForEdit] = useState<AccountSummary | null>(null);
  const [selectedTransaction, setSelectedTransaction] = useState<TransactionSummary | null>(null);
  const [selectedAccountForClosure, setSelectedAccountForClosure] = useState<AccountSummary | null>(null);

  // Extraer parámetros de URL
  const page = parseInt(searchParams.get('page') || '0', 10);
  const search = searchParams.get('search') || '';
  const statusFilter = (searchParams.get('estado') as AccountStatus) || undefined;

  // Query de cuentas
  const {
    data: accountsData,
    isLoading,
    error,
    refetch,
  } = useAccounts({
    page,
    size: PAGE_SIZE,
    search: search || undefined,
    estado: statusFilter,
  });

  // Mutation de export
  const exportMutation = useExportAccountsCsv();

  // Verificar si es PSP_ADMIN para editar estado (RULE-29)
  const canEditStatus = user?.roles?.includes('PSP_ADMIN') ?? false;
  // PSP_ADMIN puede iniciar cierre involuntario (HU-15-FE)
  const canCloseAccount = canEditStatus;

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleSearch = useCallback(
    (newSearch: string) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (newSearch) {
          params.set('search', newSearch);
        } else {
          params.delete('search');
        }
        params.set('page', '0');
        return params;
      });
    },
    [setSearchParams],
  );

  const handleStatusFilter = useCallback(
    (status: AccountStatus | undefined) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (status) {
          params.set('estado', status);
        } else {
          params.delete('estado');
        }
        params.set('page', '0');
        return params;
      });
    },
    [setSearchParams],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set('page', String(newPage));
        return params;
      });
    },
    [setSearchParams],
  );

  const handleExport = useCallback(() => {
    exportMutation.mutate({
      estado: statusFilter,
      search: search || undefined,
    });
  }, [exportMutation, statusFilter, search]);

  const handleViewDetail = useCallback((account: AccountSummary) => {
    // TODO: Navegar a página de detalle
    console.log('View detail:', account.id);
  }, []);

  const handleViewHistory = useCallback((account: AccountSummary) => {
    setSelectedAccountForHistory(account);
  }, []);

  const handleEditStatus = useCallback((account: AccountSummary) => {
    setSelectedAccountForEdit(account);
  }, []);

  const handleViewTransaction = useCallback((transaction: TransactionSummary) => {
    setSelectedTransaction(transaction);
  }, []);

  const handleStatusUpdateSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  // Handlers para cierre involuntario (HU-15-FE)
  const handleCloseAccount = useCallback((account: AccountSummary) => {
    setSelectedAccountForClosure(account);
  }, []);

  const handleClosureSuccess = useCallback((_closureId: string) => {
    // Redirigir a pantalla de estado de cierre (HU-16-FE)
    if (selectedAccountForClosure) {
      navigate(ROUTES.CLOSURE_STATUS.replace(':accountId', selectedAccountForClosure.id));
    }
  }, [navigate, selectedAccountForClosure]);

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="container py-6">
      {/* Header */}
      <div className="mb-6">
        <Eyebrow>{t('accounts:breadcrumb', 'Backoffice Operativo')}</Eyebrow>
        <h1 className="text-2xl font-bold">{t('accounts:title', 'Cuentas')}</h1>
        <p className="text-muted-foreground">
          {t('accounts:subtitle', 'Gestión de cuentas de pago (CVU)')}
        </p>
      </div>

      {/* Filtros */}
      <AccountFilters
        onSearch={handleSearch}
        onStatusFilter={handleStatusFilter}
        onExport={handleExport}
        isExporting={exportMutation.isPending}
        currentSearch={search}
        currentStatus={statusFilter}
      />

      {/* Error state */}
      {error && (
        <div className="mb-6 rounded-lg border border-destructive/50 bg-destructive/10 p-4">
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-5 w-5" />
            <span className="font-medium">
              {t('accounts:errors.loadFailed', 'Error al cargar cuentas')}
            </span>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-3">
            {t('common:retry', 'Reintentar')}
          </Button>
        </div>
      )}

      {/* Tabla */}
      <AccountListTable
        accounts={accountsData?.content || []}
        isLoading={isLoading}
        onViewDetail={handleViewDetail}
        onViewHistory={handleViewHistory}
        onEditStatus={handleEditStatus}
        canEditStatus={canEditStatus}
        onCloseAccount={handleCloseAccount}
        canCloseAccount={canCloseAccount}
        currentPage={page}
        totalPages={accountsData?.totalPages || 0}
        onPageChange={handlePageChange}
      />

      {/* Total de resultados */}
      {accountsData && (
        <p className="mt-4 text-sm text-muted-foreground">
          {t('accounts:list.total', 'Mostrando {{count}} de {{total}} cuentas', {
            count: accountsData.content.length,
            total: accountsData.totalElements,
          })}
        </p>
      )}

      {/* Modal de historial */}
      <AccountHistoryModal
        account={selectedAccountForHistory}
        open={selectedAccountForHistory !== null}
        onClose={() => setSelectedAccountForHistory(null)}
        onViewTransaction={handleViewTransaction}
      />

      {/* Modal de edición de estado */}
      <AccountEditStatusModal
        account={selectedAccountForEdit}
        open={selectedAccountForEdit !== null}
        onClose={() => setSelectedAccountForEdit(null)}
        onSuccess={handleStatusUpdateSuccess}
      />

      {/* Modal de detalle de transacción */}
      <TransactionDetailModal
        accountId={selectedAccountForHistory?.id}
        transaction={selectedTransaction}
        open={selectedTransaction !== null}
        onClose={() => setSelectedTransaction(null)}
      />

      {/* Modal de cierre involuntario (HU-15-FE) — Solo PSP_ADMIN */}
      {canCloseAccount && (
        <CloseAccountModal
          accountId={selectedAccountForClosure?.id ?? ''}
          accountHolder={selectedAccountForClosure?.merchantRazonSocial}
          cvu={selectedAccountForClosure?.cvu}
          open={selectedAccountForClosure !== null}
          onOpenChange={(open) => !open && setSelectedAccountForClosure(null)}
          onSuccess={handleClosureSuccess}
        />
      )}
    </div>
  );
}
